import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import { useLoadable, ErrorBlock } from '../components/ui'
import type { Leaderboard, LeaderboardRow } from '../types'

/* Migration leaderboard — every signed-in member of staff. Departments ranked by files brought
   into RMS (no countdown since the 30 Oct deadline). Department totals only, from the nightly Drive
   load; OpCos are never ranked. Bars are ink: the accent is kept for the one action, moving files. */

const day = (d: string, year = true) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC' })

const eatTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Dar_es_Salaam' })

/* "Updated 27 Oct, 14:00" when a same-day refresh is included, else the last full day loaded. */
const updatedLine = (r: Leaderboard) =>
  r.asOf ? `Updated ${eatTime(r.asOf)} EAT` : r.observedThrough ? `Updated to ${day(r.observedThrough)}` : 'Not yet loaded'

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="stat">
      <p className="stat__label">{label}</p>
      <p className="stat__value">{value}</p>
      <p className="stat__sub">{sub}</p>
    </div>
  )
}

function Row({ r, max }: { r: LeaderboardRow; max: number }) {
  const cls = r.files === 0 ? 'board--zero' : r.rank <= 3 ? 'board--top' : undefined
  return (
    <tr className={cls}>
      <td className="board__rank">{r.files ? r.rank : '—'}</td>
      <td>
        <span className="board__dept">{r.department}</span>
        {r.head && <span className="board__head">{r.head}</span>}
      </td>
      <td className="board__bar" aria-hidden="true">
        <div className="board__track"><div className="board__fill" style={{ width: `${max ? (r.files / max) * 100 : 0}%` }} /></div>
      </td>
      <td className="num">{r.files.toLocaleString()}</td>
      <td className="num">{r.lastWeek ? `+${r.lastWeek.toLocaleString()}` : '—'}</td>
      <td className="num">{r.movers}</td>
    </tr>
  )
}

/* Start Moving Files one-pager (GLB_RMS_StartMovingFiles_20261022). */
const MOVING_GUIDE = 'https://docs.google.com/document/d/1ARDLAYctgecT2QoS75oi6537sUQFvT7dxyErNu9WShw/edit'
const NUDGE = 'Once your files are moved, make working from RMS your default.' // same line as the daily Chat post

/* Screenshot view (?view=share): one fixed-width card, no site header, sized to post in the
   rms-help Chat space. Departments still at zero are named on one line instead of listed. */
function ShareCard({ r, max }: { r: Leaderboard; max: number }) {
  const moving = r.rows.filter((x) => x.files > 0)
  const waiting = r.rows.filter((x) => x.files === 0 && x.department !== 'Executives') // Tony, 26 Oct: execs never called out
  return (
    <div className="lbshare">
      <div className="lbshare__top">
        <span className="brand"><span className="dot" aria-hidden="true" />RMS</span>
        <span className="lbshare__asof">{updatedLine(r)}</span>
      </div>
      <h1 className="lbshare__title">Migration leaderboard</h1>
      <p className="lbshare__sub">Files moved into RMS, by department</p>
      <div className="lbshare__nums">
        <div><span className="lbshare__big">{r.totalFiles.toLocaleString()}</span><span className="lbshare__lbl">files moved so far</span></div>
      </div>
      <ol className="lbshare__list">
        {moving.map((x) => (
          <li key={x.department} className={x.rank <= 3 ? 'lbshare__row lbshare__row--top' : 'lbshare__row'}>
            <span className="lbshare__rank">{x.rank}</span>
            <span className="lbshare__dept">{x.department}{x.head && <span className="lbshare__head">{x.head}</span>}</span>
            <span className="board__track" aria-hidden="true"><span className="board__fill" style={{ display: 'block', width: `${max ? (x.files / max) * 100 : 0}%` }} /></span>
            <span className="lbshare__files">{x.files.toLocaleString()}</span>
          </li>
        ))}
      </ol>
      {waiting.length > 0 && <p className="lbshare__waiting"><b>Yet to start:</b> {waiting.map((x) => x.department).join(', ')}</p>}
      <p className="lbshare__foot">
        <b>Move your files today, it's simple:</b> follow <a href={MOVING_GUIDE} target="_blank" rel="noreferrer">this guide</a>.
        Need guidance? Ask for it in the Google Chat space <b>rms-help</b>.
      </p>
      <p className="lbshare__foot">{NUDGE}</p>
    </div>
  )
}

export default function LeaderboardRoute() {
  const [lb, retry] = useLoadable<Leaderboard>(() => api.getLeaderboard(), () => false, [])
  const [params] = useSearchParams()
  const share = params.get('view') === 'share'
  useEffect(() => {
    document.body.classList.toggle('lb-share', share)
    return () => document.body.classList.remove('lb-share')
  }, [share])
  const r = lb.status === 'ready' ? lb.data : null
  const max = r ? Math.max(0, ...r.rows.map((x) => x.files)) : 0
  const leader = r?.rows.find((x) => x.files > 0)
  const moving = r ? r.rows.filter((x) => x.files > 0).length : 0

  if (share && r) {
    return (
      <>
        <ShareCard r={r} max={max} />
        <p className="lbshare__back"><Link to="/leaderboard">← Back to the full leaderboard</Link></p>
      </>
    )
  }

  return (
    <>
      <p className="eyebrow">File migration</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>Which department has moved the most into RMS?</h1>
      <p className="note" style={{ marginBottom: 'var(--s-md)' }}>
        Files moved or uploaded into the RMS drives from My Drive and old shared drives, credited to the
        department of the person who moved them. Refreshed every hour, 7am to 8pm EAT.
      </p>

      {lb.status === 'error' && <ErrorBlock message={lb.message} correlationId={lb.correlationId} onRetry={retry} />}
      {lb.status === 'loading' && (
        <div aria-busy="true">
          <div className="skeleton" style={{ height: '7rem', marginBottom: 12 }} />
          <div className="skeleton" style={{ height: '20rem' }} />
        </div>
      )}

      {r && (
        <>
          <div className="stats stats--2" style={{ marginBottom: 'var(--s-lg)' }}>
            <Stat label="Files moved into RMS" value={r.observedFrom ? r.totalFiles.toLocaleString() : '—'}
                  sub={r.observedFrom ? `Since ${day(r.observedFrom, false)} · ${updatedLine(r)}` : 'Not yet loaded'} />
            <Stat label="In the lead" value={leader ? leader.department : '—'}
                  sub={leader ? `${leader.files.toLocaleString()} files · ${moving} of ${r.rows.length} departments moving` : 'No files moved yet'} />
          </div>

          <p style={{ marginBottom: 'var(--s-lg)' }}>
            <Link className="btn btn--primary" to="/my-access">Move your files →</Link>{' '}
            <Link className="btn btn--secondary" to="/leaderboard?view=share">Screenshot view</Link>
          </p>
          <p className="note" style={{ marginBottom: 'var(--s-lg)' }}>{NUDGE}</p>

          <div className="tablewrap board" style={{ marginBottom: 'var(--s-md)' }}>
            <table style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  <th>#</th><th>Department</th><th><span className="sr-only">Share</span></th>
                  <th style={{ textAlign: 'right' }}>Files</th>
                  <th style={{ textAlign: 'right' }}>Last 7 days</th>
                  <th style={{ textAlign: 'right' }}>People moving</th>
                </tr>
              </thead>
              <tbody>{r.rows.map((x) => <Row key={x.department} r={x} max={max} />)}</tbody>
            </table>
          </div>

          <p className="note" style={{ marginBottom: 'var(--s-xs)' }}>
            Each file counts once. Departments and heads follow the 2026 organogram; your department comes from the RMS training roster.
            Today's figures are provisional until the overnight check; Google can take up to a few hours to report a move.
          </p>
          {(r.notOnRoster > 0 || r.noNamedPerson > 0) && (
            <p className="note" style={{ marginBottom: 'var(--s-xs)' }}>
              Not ranked: {(r.notOnRoster + r.noNamedPerson).toLocaleString()} files moved by accounts not on the roster.
            </p>
          )}
          {r.missingDates.length > 0 && (
            <p className="note" style={{ marginBottom: 0 }}>
              <b>Incomplete:</b> Drive activity for {r.missingDates.map((d) => day(d, false)).join(', ')} is not loaded yet, so these totals are a minimum.
            </p>
          )}
        </>
      )}
    </>
  )
}
